// The client onboarding questionnaire — single source of truth shared by the
// portal form (renders it), the save route (validates against it), the team
// notify email + PRD bundle (summarize it). Adapted from the agency's
// standalone form (templates/client-intake-form.html) with one build-scope
// question added, since lander projects span websites, web apps, and mobile
// apps.
//
// Answer shape on client-intakes.answers: flat { [field.key]: string |
// string[] } — string[] only for `checkboxes` fields. sanitizeAnswers()
// below is the only way answers are ever written.

export type QuestionnaireField = {
  key: string
  label: string
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'radio' | 'checkboxes'
  hint?: string
  optional?: boolean
  options?: string[]
  /** Placeholder-style example shown under the label (textarea formats). */
  example?: string
}

export type QuestionnaireSection = {
  key: string
  title: string
  why: string
  fields: QuestionnaireField[]
}

const FREE_TEXT_MAX = 4000
const SHORT_TEXT_MAX = 500

export const QUESTIONNAIRE_SECTIONS: QuestionnaireSection[] = [
  {
    key: 'business',
    title: 'Your business',
    why: 'The basics that anchor everything we build for you.',
    fields: [
      { key: 'businessName', label: 'Business name — exactly as it should appear', type: 'text' },
      {
        key: 'buildType',
        label: 'What are we building together?',
        type: 'radio',
        options: ['A website', 'A web application', 'A mobile app', 'Website + app', 'Not sure yet'],
      },
      {
        key: 'businessKind',
        label: 'What does the business do?',
        type: 'textarea',
        hint: 'A sentence or two, the way you would tell a customer.',
      },
      { key: 'address', label: 'Business address', type: 'text', optional: true },
      { key: 'phone', label: 'Business phone number', type: 'tel', optional: true },
      {
        key: 'hours',
        label: 'Opening hours',
        type: 'text',
        optional: true,
        hint: 'However you would say it, e.g. "Mon–Fri 9–6, closed Sunday."',
      },
    ],
  },
  {
    key: 'contact',
    title: 'Reaching you',
    why: 'Where messages should land, and who runs things day to day.',
    fields: [
      {
        key: 'enquiriesEmail',
        label: 'Which email should receive enquiries?',
        type: 'email',
        hint: 'New messages, booking requests, and alerts go here.',
      },
      {
        key: 'siteManager',
        label: 'Who will manage it day to day?',
        type: 'text',
        hint: 'Name and email — this is who we set up with a login.',
      },
    ],
  },
  {
    key: 'domain',
    title: 'Your web address',
    why: 'The ".com" your customers will type in.',
    fields: [
      {
        key: 'domainSituation',
        label: 'Do you already own a domain name?',
        type: 'radio',
        options: ['Yes — I own one', 'No — help me pick and register one', 'Not sure'],
      },
      {
        key: 'domainDetails',
        label: "If yes: what's the address, and where did you buy it?",
        type: 'text',
        optional: true,
        hint: 'e.g. "fadedistrict.com, bought on GoDaddy."',
      },
    ],
  },
  {
    key: 'brand',
    title: 'Your look',
    why: "Logo and style. If you don't have these, that's fine — we tune the design to you.",
    fields: [
      {
        key: 'logoSituation',
        label: 'Logo',
        type: 'radio',
        options: [
          'I have one — uploading it below',
          'I need one made',
          'Just use my business name in a nice typeface',
        ],
      },
      {
        key: 'styleNotes',
        label: 'Colors, styles, or sites you like',
        type: 'textarea',
        optional: true,
        hint: 'Links to your current site, social profiles, or a competitor site you like the feel of.',
      },
    ],
  },
  {
    key: 'services',
    title: 'What you sell',
    why: 'Your services and prices become the heart of the build.',
    fields: [
      {
        key: 'servicesList',
        label: 'List your services or products with prices — aim for 4 to 6',
        type: 'textarea',
        example: 'One per line, like: Haircut & beard trim — $45, or AC tune-up — from $129.',
      },
      {
        key: 'deposits',
        label: 'Should any require a deposit or payment up front?',
        type: 'text',
        optional: true,
      },
    ],
  },
  {
    key: 'team',
    title: 'Your team',
    why: 'Optional — team pages build trust, and customers can book with specific people.',
    fields: [
      {
        key: 'teamMembers',
        label: 'Who should appear?',
        type: 'textarea',
        optional: true,
        hint: 'Name, what they do, a sentence about them. Photos: upload them below.',
      },
    ],
  },
  {
    key: 'areas',
    title: 'Where you work',
    why: 'For businesses that go to the customer — each area can get its own page.',
    fields: [
      {
        key: 'serviceAreas',
        label: 'Towns or neighborhoods you serve',
        type: 'textarea',
        optional: true,
        hint: 'Skip this if customers come to you.',
      },
    ],
  },
  {
    key: 'reviews',
    title: 'Reviews',
    why: 'Happy customers can be nudged to leave a Google review — we just need to know where to send them.',
    fields: [
      {
        key: 'googleReviews',
        label: 'Google reviews',
        type: 'radio',
        options: [
          "We're on Google — link below",
          "We're on Google but I don't have a link handy",
          "We're not on Google yet — help us set that up",
        ],
      },
      { key: 'googleLink', label: 'Link or business name on Google', type: 'text', optional: true },
    ],
  },
  {
    key: 'extras',
    title: 'Extras',
    why: "Check anything you'd like — we'll cover how each works before anything is switched on.",
    fields: [
      {
        key: 'extras',
        label: 'Interested in any of these?',
        type: 'checkboxes',
        optional: true,
        options: [
          'Text-message updates & reminders',
          'Online deposits / card payments',
          'Done-for-you blog & newsletter',
          'Careers page',
          'Offers, events, or wholesale',
          'Customer accounts / logins',
        ],
      },
      {
        key: 'extrasNotes',
        label: 'Anything specific about those?',
        type: 'textarea',
        optional: true,
      },
    ],
  },
  {
    key: 'anything',
    title: 'Anything else?',
    why: "The stuff a form can't ask.",
    fields: [
      {
        key: 'anythingElse',
        label: 'Goals, deadlines, must-haves, pet peeves',
        type: 'textarea',
        optional: true,
        hint: 'e.g. "open house March 1st", "my old site was impossible to update."',
      },
    ],
  },
]

const FIELD_BY_KEY: Map<string, QuestionnaireField> = new Map(
  QUESTIONNAIRE_SECTIONS.flatMap((s) => s.fields).map((f) => [f.key, f]),
)

export type IntakeAnswers = Record<string, string | string[]>

// Validates + normalizes ONE field's value against the schema (trim, length
// cap, option check for radio/select). Returns the normalized string, or
// null when the key/type/value doesn't belong on the schema at all.
//
// An explicit '' is a VALID return (not null) for non-checkbox fields — it
// represents "touched, deliberately cleared." Core semantics (client-intake
// prefill spec): a key present in `answers`, even as '', is touched/frozen
// and must never be re-prefilled; only a genuinely ABSENT key is untouched.
// An empty value skips the option check (there's nothing to validate against
// for "cleared"), so clearing a radio/select field is preserved too, not
// silently dropped back to "untouched."
//
// Shared by sanitizeAnswers (the whole-object write gate below) and the
// prefill resolver (src/lib/client-intake/prefill.ts) — one implementation
// of "is this a legal value for this field" for both writers.
export function sanitizeFieldValue(key: string, raw: unknown): string | null {
  const field = FIELD_BY_KEY.get(key)
  if (!field || field.type === 'checkboxes') return null
  if (typeof raw !== 'string') return null
  const max = field.type === 'textarea' ? FREE_TEXT_MAX : SHORT_TEXT_MAX
  const value = raw.trim().slice(0, max)
  if (value && (field.type === 'radio' || field.type === 'select') && field.options) {
    if (!field.options.includes(value)) return null
  }
  return value
}

// Whitelist + cap the incoming answers object against the schema. Unknown
// keys are dropped, values coerced to trimmed strings (arrays only for
// checkboxes fields, restricted to the declared options). A key present in
// the input with an empty/cleared value is KEPT (touched/frozen — see
// sanitizeFieldValue above), not dropped; only unknown keys and
// type-mismatched/invalid-option values are dropped. Never throws.
export function sanitizeAnswers(input: unknown): IntakeAnswers {
  const out: IntakeAnswers = {}
  if (!input || typeof input !== 'object' || Array.isArray(input)) return out
  for (const [key, raw] of Object.entries(input as Record<string, unknown>)) {
    const field = FIELD_BY_KEY.get(key)
    if (!field) continue
    if (field.type === 'checkboxes') {
      if (!Array.isArray(raw)) continue
      const allowed = new Set(field.options || [])
      const values = raw
        .filter((v): v is string => typeof v === 'string')
        .map((v) => v.trim())
        .filter((v) => allowed.has(v))
      out[key] = values
      continue
    }
    const value = sanitizeFieldValue(key, raw)
    if (value === null) continue
    out[key] = value
  }
  return out
}

// Plain-text rendering for the team notify email and anywhere a human (or
// agent) reads the answers as prose rather than json.
export function renderAnswersText(answers: IntakeAnswers | null | undefined): string {
  const a = answers || {}
  const lines: string[] = []
  QUESTIONNAIRE_SECTIONS.forEach((section, i) => {
    const body: string[] = []
    for (const field of section.fields) {
      const value = a[field.key]
      if (value === undefined) continue
      const rendered = Array.isArray(value) ? value.join('; ') : value
      if (!rendered) continue
      body.push(`${field.label}: ${rendered}`)
    }
    lines.push(`${String(i + 1).padStart(2, '0')} — ${section.title}`)
    lines.push(body.length ? body.join('\n') : '(skipped)')
    lines.push('')
  })
  return lines.join('\n').trim()
}
